import { Controller } from "@hotwired/stimulus"
import { speakWithLipSync } from "@primeta/persona-core"

export default class extends Controller {
  static targets = ["selectedVoiceId", "selectedVoiceName", "audioPlayer", "playSelectedBtn", "customVoiceId"]
  static values = { intro: String }

  _avatarController() {
    const el = document.querySelector("[data-controller~='avatar']")
    return el?.avatarController
  }

  disconnect() {
    this._currentSpeech?.cancel()
  }

  select(event) {
    const voiceId = event.currentTarget.dataset.voiceId
    const voiceName = event.currentTarget.dataset.voiceName

    this.selectedVoiceIdTarget.value = voiceId
    this.selectedVoiceNameTarget.textContent = voiceName
    if (this.hasPlaySelectedBtnTarget) {
      this.playSelectedBtnTarget.classList.remove("opacity-40", "pointer-events-none")
    }

    // Highlight selected card
    this.element.querySelectorAll("[data-voice-card]").forEach(card => {
      const isSelected = card.dataset.voiceId === voiceId
      card.classList.toggle("border-accent", isSelected)
      card.classList.toggle("shadow-md", isSelected)
    })
  }

  selectCustom() {
    const voiceId = this.customVoiceIdTarget.value.trim()
    if (!voiceId) return

    this.selectedVoiceIdTarget.value = voiceId
    this.selectedVoiceNameTarget.textContent = voiceId.substring(0, 24)
    if (this.hasPlaySelectedBtnTarget) {
      this.playSelectedBtnTarget.classList.remove("opacity-40", "pointer-events-none")
    }

    // Clear library selection
    this.element.querySelectorAll("[data-voice-card]").forEach(card => {
      card.classList.remove("border-accent", "shadow-md")
    })
  }

  previewSelected(event) {
    const voiceId = this.selectedVoiceIdTarget.value
    if (!voiceId) return
    event.currentTarget.dataset.voiceId = voiceId
    return this.previewIntro(event)
  }

  async previewIntro(event) {
    const voiceId = event.currentTarget.dataset.voiceId
    if (!voiceId || !this.introValue) return

    const btn = event.currentTarget
    const originalHTML = btn.innerHTML
    btn.innerHTML = '<svg class="animate-spin w-3 h-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>'
    btn.disabled = true

    try {
      this._currentSpeech?.cancel()
      const response = await fetch(`/voice_preview/${voiceId}.json?text=${encodeURIComponent(this.introValue)}`)
      if (!response.ok) throw new Error("preview failed")
      const data = await response.json()

      const ctrl = this._avatarController()
      const vrm = ctrl?._avatar?.vrm

      if (!this._audioCtx) this._audioCtx = new AudioContext()
      if (this._audioCtx.state === 'suspended') await this._audioCtx.resume()

      if (vrm?.expressionManager) {
        // VRM available — phoneme-driven lipsync
        this._currentSpeech = speakWithLipSync({
          audioBase64: data.audio_base64,
          phonemes: data.phonemes,
          vrm,
          avatar: ctrl?._avatar,
          audioCtx: this._audioCtx,
          playAnimation: (name) => ctrl?.playAnimation?.(name),
          onDone: () => { this._currentSpeech = null }
        })
      } else {
        // No VRM — play through <audio> element (bypasses iOS silent mode)
        const raw = atob(data.audio_base64)
        const buffer = new ArrayBuffer(raw.length)
        const view = new Uint8Array(buffer)
        for (let i = 0; i < raw.length; i++) view[i] = raw.charCodeAt(i)
        const blob = new Blob([buffer], { type: 'audio/mpeg' })
        const blobUrl = URL.createObjectURL(blob)
        const audio = new Audio(blobUrl)
        const sourceNode = this._audioCtx.createMediaElementSource(audio)
        sourceNode.connect(this._audioCtx.destination)
        audio.onended = () => { URL.revokeObjectURL(blobUrl); this._currentSpeech = null }
        await audio.play()
        this._currentSpeech = { cancel: () => { audio.pause(); audio.src = ''; URL.revokeObjectURL(blobUrl); this._currentSpeech = null } }
      }
    } catch (err) {
      console.error("[voice-picker]", err)
    } finally {
      btn.innerHTML = originalHTML
      btn.disabled = false
    }
  }

  toggleTag(event) {
    const tag = event.currentTarget.dataset.voiceTag
    const url = new URL(window.location.href)
    const current = url.searchParams.getAll("voice_tag[]")
    url.searchParams.delete("voice_tag[]")
    url.searchParams.delete("voice_tag")
    const next = current.includes(tag) ? current.filter(t => t !== tag) : [...current, tag]
    next.forEach(t => url.searchParams.append("voice_tag[]", t))
    url.searchParams.set("voice_page", "1")
    url.searchParams.set("step", "2")
    Turbo.visit(url.toString())
  }

  clearTags() {
    const url = new URL(window.location.href)
    url.searchParams.delete("voice_tag[]")
    url.searchParams.delete("voice_tag")
    url.searchParams.set("voice_page", "1")
    url.searchParams.set("step", "2")
    Turbo.visit(url.toString())
  }

  search(event) {
    if (event.type === "keydown" && event.key !== "Enter") return
    event.preventDefault()

    const input = this.element.querySelector("input[name='voice_q']")
    if (!input) return

    const url = new URL(window.location.href)
    url.searchParams.set("voice_q", input.value)
    url.searchParams.set("voice_page", "1")
    url.searchParams.set("step", "2")
    Turbo.visit(url.toString())
  }
}
